import React, { useState, useContext, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import AppBadge from 'shared/badges/app-badge'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Alert from 'react-bootstrap/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDatabase, faSearch, faTable, faCheck, faMagic,
  faExclamationTriangle, faTimesCircle, faClipboardCheck, faExternalLinkAlt, faFolderOpen
} from '@fortawesome/free-solid-svg-icons'
import UserContext from 'context/user-context'
import { datasetSourcePath } from 'helpers/utils'
import { useDashboards } from 'api/hooks/use-dashboards'
import useReports from 'api/hooks/use-reports'
import { useFolders } from 'api/hooks/use-folders'
import { buildFolderTree, folderPath } from '../dashboard/folders/folder-tree'
import { useUpdateReport } from 'api/hooks/use-report-builder'
import { useAccountGroupings, defaultGrouping } from 'api/hooks/use-account-groupings'
import AccountGroupingSelect from 'dashboard/account-grouping-select'
import {
  useReportDatasets,
  useCreateReportDataset,
  useDestroyReportDataset
} from 'api/hooks/use-report-datasets'
import AiViewCreator from './ai-view-creator'

export const SOURCE_KIND = { view: 'views', sample: 'samples' }

// Where the picker looks for sources. A report's folder is its project: the Views and Samples
// filed beside it (and in the folders nested below it) are the ones it is meant to draw from, so
// that is the default. `all` is the override — every source the user can see, wherever it sits.
export const SOURCE_SCOPE = { folder: 'folder', all: 'all' }

// What the picker calls the top level, for a report that isn't filed anywhere.
export const TOP_LEVEL_LABEL = 'Top level (no folder)'

// The index params for a scope. A folder reads as its whole branch; a report with no folder reads
// the top level — the items that aren't filed anywhere, which is where it sits itself.
export const scopeParams = (scope, folderId) => {
  if (scope === SOURCE_SCOPE.all) return {}
  return folderId ? { folder_id: folderId, include_subfolders: 'true' } : { unfiled: 'true' }
}

// A dataset's identity is (source_type, source_id) — the two kinds of source live in different
// tables, so an id alone is ambiguous.
const sourceKey = (type, id) => `${type}:${id}`

const toViewItem = (view) => ({
  id: view.id,
  sourceType: 'Dashboard',
  name: view.name,
  sectionName: view.section_name,
  folderId: view.folder_id,
  pills: []
})

// A sample's useful metadata is its size and how much of it has been reviewed — an unreviewed
// sample charts as a population with no results, which is worth seeing before linking it.
const toSampleItem = (sample) => ({
  id: sample.id,
  sourceType: 'AuditReport',
  name: sample.name,
  sectionName: sample.section?.name,
  folderId: sample.folder_id,
  pills: [
    `${sample.total_count || 0} items`,
    `${sample.exception_count || 0} exceptions`,
    ...(sample.open_count ? [`${sample.open_count} open`] : [])
  ]
})

// `folderId` is the report's folder, handed in by the page that already holds the report rather
// than fetched here: a second observer on the report query would refetch it on mount, and the
// builder is always showing this modal (hidden) once the report has loaded.
function DatasetModal({ reportId, folderId = null, show, onHide, report = null, canEdit = true }) {
  // NL dataset creation calls an AI endpoint gated on the municipality's ai_search_enabled setting.
  const user = useContext(UserContext)
  const aiEnabled = user?.municipality?.setting?.ai_search_enabled ?? false
  const [search, setSearch] = useState('')
  const [showAiCreator, setShowAiCreator] = useState(false)
  const [sourceKind, setSourceKind] = useState(SOURCE_KIND.view)
  const [scope, setScope] = useState(SOURCE_SCOPE.folder)
  // A dataset whose removal is waiting on confirmation, because charts are built on it.
  const [pendingRemoval, setPendingRemoval] = useState(null)
  // '' is a real pending value (hand the choice back to each View), so not a truthiness check.
  const [pendingGroupingId, setPendingGroupingId] = useState(null)
  const queryClient = useQueryClient()

  const { data: datasets = [] } = useReportDatasets({ reportId })

  // The report's folder is the default scope, so the source lists wait for the folder tree —
  // otherwise the first request would fetch everything and the scoped list would replace it a
  // beat later. Fetched only while the modal is open.
  const { data: foldersData, isSuccess: foldersLoaded } = useFolders({ options: { enabled: show } })
  const folderTree = useMemo(() => buildFolderTree(foldersData?.data || []), [foldersData])
  const reportFolder = folderId ? folderTree.byId.get(folderId) || null : null
  // A report can sit in a folder the viewer can't see — shared with them directly while filed in
  // the owner's private folder. There is no branch to scope to then, so the override is the only
  // option and the toggle is not offered.
  const folderScopeAvailable = !folderId || !!reportFolder
  const effectiveScope = folderScopeAvailable ? scope : SOURCE_SCOPE.all
  const scopeReady = foldersLoaded
  const listParams = scopeParams(effectiveScope, folderId)

  const { data: dashboardsResponse } = useDashboards({
    params: { perPage: 500, ...listParams },
    options: { enabled: show && scopeReady }
  })
  const allViews = dashboardsResponse?.data || []
  // Samples are only fetched once the samples tab is opened — the list carries per-sample review
  // counts, so it is more expensive than the Views list.
  const { data: samplesResponse } = useReports({
    params: { perPage: 200, ...listParams },
    options: { enabled: show && scopeReady && sourceKind === SOURCE_KIND.sample }
  })
  const allSamples = samplesResponse?.data || []

  const scopedToFolder = effectiveScope === SOURCE_SCOPE.folder
  const scopeLabel = reportFolder ? folderPath(folderTree, reportFolder) : TOP_LEVEL_LABEL
  // Under the override, each source says where it is filed so crossing into another folder is a
  // choice the user can see, not something that happens by accident.
  const itemFolderLabel = (item) => {
    if (scopedToFolder) return null
    const folder = item.folderId ? folderTree.byId.get(item.folderId) : null
    return folder ? folderPath(folderTree, folder) : TOP_LEVEL_LABEL
  }

  const { mutate: linkDataset } = useCreateReportDataset({ reportId, options: {} })
  const { mutate: unlinkDataset } = useDestroyReportDataset({ reportId })
  const { data: groupings = [] } = useAccountGroupings({})
  const { mutate: updateReport, isLoading: isSavingGrouping } = useUpdateReport({ options: {} })

  // Keyed by source rather than dashboard_id: a dataset can now be backed by either a View or a
  // sample, and their ids live in different tables.
  const linkedMap = new Map(datasets.map((d) => [sourceKey(d.source_type, d.source_id), d]))

  // A dataset's effective label is what the assistant uses to refer to it (its custom label, or the
  // View name as a fallback). Two datasets are only ambiguous when they share a section AND resolve
  // to the same label — distinct names (the common case) need no warning.
  const effectiveLabel = (d) => ((d.label || '').trim() || d.dashboard_name || '').trim().toLowerCase()

  const datasetsBySection = {}
  datasets.forEach((d) => {
    if (d.section_id) (datasetsBySection[d.section_id] ||= []).push(d)
  })
  const ambiguousDatasetIds = new Set()
  Object.values(datasetsBySection).forEach((group) => {
    if (group.length < 2) return
    const labelCounts = {}
    group.forEach((d) => { labelCounts[effectiveLabel(d)] = (labelCounts[effectiveLabel(d)] || 0) + 1 })
    group.forEach((d) => { if (labelCounts[effectiveLabel(d)] > 1) ambiguousDatasetIds.add(d.id) })
  })
  const hasDuplicates = ambiguousDatasetIds.size > 0

  // A declared grouping wins over every linked View; undeclared, each View decides for itself.
  const declaredGroupingId = report?.account_grouping_id || ''
  const districtDefaultId = defaultGrouping(groupings)?.id || null

  // Undeclared, two Views under different taxonomies put two differently-bucketed exhibits
  // headed "Object Group" in one document.
  const linkedGroupingIds = new Set(
    datasets
      .filter((dataset) => !dataset.is_sample)
      .map((dataset) => dataset.account_grouping_id || districtDefaultId)
      .filter(Boolean)
  )
  const hasMixedGroupings = !declaredGroupingId && linkedGroupingIds.size > 1

  const groupingName = (id) =>
    groupings.find((grouping) => grouping.id === id)?.name || 'the district default'

  // Charts re-bucket on their next read, but a computed exhibit is frozen text — say so first.
  const confirmGrouping = () => {
    if (pendingGroupingId === null) return
    updateReport({ id: reportId, account_grouping_id: pendingGroupingId || null }, {
      onSuccess: () => {
        queryClient.invalidateQueries([`reports/${reportId}`])
        queryClient.invalidateQueries(['reports-builder'])
        // Every chart re-buckets and each block's stale flag is recomputed server-side, and
        // that flag reaches the page only through the grid-rows response.
        queryClient.invalidateQueries('report-chart-data')
        queryClient.invalidateQueries([`reports/${reportId}/report_charts`])
        queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
        setPendingGroupingId(null)
        toast.success('Account grouping updated')
      },
      onError: () => toast.error('Could not change the account grouping')
    })
  }

  const showingSamples = sourceKind === SOURCE_KIND.sample
  const sourceItems = showingSamples ? allSamples.map(toSampleItem) : allViews.map(toViewItem)

  const filteredItems = sourceItems.filter((item) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      item.name?.toLowerCase().includes(q) ||
      item.sectionName?.toLowerCase().includes(q)
    )
  })

  // source_name covers a sample, which has no dashboard_name.
  const datasetLabel = (dataset) =>
    dataset.display_label || dataset.label || dataset.source_name || dataset.dashboard_name ||
    'this data source'

  // Removing a dataset unbinds every chart built on it, and re-linking the same source afterwards
  // creates a *new* dataset row rather than restoring the old binding — so those charts have to be
  // reconnected one by one. Too destructive for a single click; a dataset no chart uses is a plain
  // toggle.
  const requestUnlink = (dataset) => {
    if ((dataset.report_charts_count || 0) > 0) {
      setPendingRemoval(dataset)
      return
    }
    unlinkDataset(dataset.id)
  }

  const confirmUnlink = () => {
    if (!pendingRemoval) return
    unlinkDataset(pendingRemoval.id)
    setPendingRemoval(null)
  }

  const handleToggle = (item) => {
    const linked = linkedMap.get(sourceKey(item.sourceType, item.id))
    if (linked) {
      requestUnlink(linked)
    } else {
      linkDataset(item.sourceType === 'AuditReport' ? { audit_report_id: item.id } : { dashboard_id: item.id })
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="px-4 pt-4 pb-3 border-bottom align-items-start">
        <div className="d-flex align-items-center gap-3 flex-grow-1">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{ width: 52, height: 52, backgroundColor: '#1E2E48' }}
          >
            <FontAwesomeIcon icon={faDatabase} className="text-warning" />
          </div>
          <div>
            <h5 className="fw-bold mb-0">Data sources</h5>
            <p className="text-muted small mb-0">
              Select the saved views and samples to include as datasets for this report.
            </p>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 ms-3">
          <span
            className="border rounded-2 px-3 py-1 text-muted small"
            style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
          >
            {datasets.length} linked
          </span>
          <Button
            variant="light"
            className="border rounded-2 lh-1 p-1"
            style={{ width: 32, height: 32 }}
            onClick={onHide}
            aria-label="Close"
          >
            ×
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 pt-3 pb-0" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        {pendingRemoval && (
          <Alert
            variant="warning"
            className="py-2 px-3 mb-3"
            style={{ position: 'sticky', top: 0, zIndex: 5 }}
          >
            <div className="d-flex align-items-start gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 flex-shrink-0" />
              <div className="flex-grow-1 small">
                <div className="fw-semibold mb-1">
                  Remove “{datasetLabel(pendingRemoval)}”?
                </div>
                {pendingRemoval.report_charts_count === 1
                  ? '1 component in this report draws from it and will stop rendering.'
                  : `${pendingRemoval.report_charts_count} components in this report draw from it and will stop rendering.`}{' '}
                Linking this view again later creates a new data source rather than restoring this
                one, so each component has to be reconnected from its own settings.
              </div>
              <div className="d-flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="light"
                  className="border text-primary"
                  onClick={() => setPendingRemoval(null)}
                >
                  Keep it
                </Button>
                <Button size="sm" variant="danger" onClick={confirmUnlink}>
                  Remove anyway
                </Button>
              </div>
            </div>
          </Alert>
        )}

        {groupings.length > 1 && (
          <div className="mb-4">
            <div
              className="text-muted fw-semibold mb-2 text-uppercase"
              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
            >
              Account grouping
            </div>

            <AccountGroupingSelect
              // The pending pick: the saved value would snap the dropdown back mid-confirm.
              value={pendingGroupingId ?? declaredGroupingId}
              disabled={!canEdit || isSavingGrouping}
              controlId="reportAccountGrouping"
              label={null}
              ariaLabel="Account grouping for this report"
              testId="report-grouping-select"
              inheritLabel={"Each view's own choice"}
              helpText="The grouping every component in this report rolls accounts up under. Set one to report the same views under a second grouping without duplicating them."
              onChange={(id) => setPendingGroupingId(id === declaredGroupingId ? null : id)}
            />

            {pendingGroupingId !== null && (
              <Alert variant="warning" className="py-2 px-3 mt-2 mb-0">
                <div className="d-flex align-items-start gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 flex-shrink-0" />
                  <div className="flex-grow-1 small">
                    <div className="fw-semibold mb-1">
                      {pendingGroupingId
                        ? `Report under ${groupingName(pendingGroupingId)}?`
                        : 'Let each view choose again?'}
                    </div>
                    Every component re-buckets its rollup columns. Tables whose figures were
                    computed once are not recalculated, so they keep the old grouping and will be
                    flagged as out of date until they are rebuilt.
                  </div>
                  <div className="d-flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="light"
                      className="border text-primary"
                      onClick={() => setPendingGroupingId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={isSavingGrouping}
                      onClick={confirmGrouping}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </Alert>
            )}

            {hasMixedGroupings && pendingGroupingId === null && (
              <Alert variant="warning" className="py-2 px-3 mt-2 mb-0 d-flex align-items-start gap-2 small">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 flex-shrink-0" />
                <div>
                  The linked views report under different account groupings, so this report mixes
                  taxonomies — two components can label a column the same way and bucket it
                  differently. Choose one above to settle it for the whole report.
                </div>
              </Alert>
            )}
          </div>
        )}

        {datasets.length > 0 && (
          <div className="mb-3">
            <div
              className="text-muted fw-semibold mb-2 text-uppercase"
              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
            >
              Linked datasets
            </div>

            {hasDuplicates && (
              <Alert variant="warning" className="py-2 px-3 mb-2 d-flex align-items-center gap-2 small">
                <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                Two datasets in the same section share a name — rename one of the underlying sources so the assistant can tell them apart.
              </Alert>
            )}

            <div className="d-flex flex-column gap-2 mb-3">
              {datasets.map((dataset, i) => {
                const isDuplicate = ambiguousDatasetIds.has(dataset.id)
                // Read-only: a dataset is named by the View behind it, so it's renamed there.
                const labelValue = dataset.display_label || dataset.label || dataset.dashboard_name || ''
                // The source behind the dataset, openable in its own tab so a View's filters can be
                // checked (or a sample's reviews read) without losing the report and this modal.
                // Empty when the source can't be resolved — see datasetSourcePath — in which case
                // the name stays plain text rather than a dead link.
                const href = datasetSourcePath(dataset)
                return (
                  <div
                    key={dataset.id}
                    className={`d-flex align-items-center gap-2 rounded-3 px-3 py-2 border${isDuplicate ? ' border-warning' : ''}`}
                    style={{ backgroundColor: isDuplicate ? '#fffdf0' : undefined }}
                  >
                    <span
                      className="text-muted flex-shrink-0"
                      style={{ width: 30, fontSize: '0.75rem', fontFamily: 'monospace' }}
                    >
                      DS{i + 1}
                    </span>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-grow-1 d-flex align-items-center gap-2 text-decoration-none"
                        style={{ maxWidth: 280, fontSize: '0.875rem' }}
                        title={`Open ${labelValue} in a new tab`}
                      >
                        <span className="text-truncate">{labelValue}</span>
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="flex-shrink-0"
                          style={{ fontSize: '0.7rem' }}
                        />
                      </a>
                    ) : (
                      <span
                        className="flex-grow-1 text-truncate"
                        style={{ maxWidth: 280, fontSize: '0.875rem' }}
                        title={labelValue}
                      >
                        {labelValue}
                      </span>
                    )}
                    {dataset.is_sample && (
                      <AppBadge variant="primary" className="flex-shrink-0">Sample</AppBadge>
                    )}
                    {dataset.section?.name && (
                      <span className="text-muted small flex-shrink-0">
                        {dataset.section.name}
                      </span>
                    )}
                    {isDuplicate && (
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="text-warning flex-shrink-0"
                        size="sm"
                        title="Shares a section with another linked dataset"
                      />
                    )}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 ms-auto flex-shrink-0 text-muted"
                      onClick={() => requestUnlink(dataset)}
                      aria-label={`Remove ${dataset.display_label}`}
                    >
                      <FontAwesomeIcon icon={faTimesCircle} size="sm" />
                    </button>
                  </div>
                )
              })}
            </div>

            <hr className="my-0 mb-3" />
          </div>
        )}

        {scopeReady && (
          <div
            className="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-3 border bg-light small"
            data-testid="dataset-scope"
          >
            <FontAwesomeIcon icon={faFolderOpen} className="text-muted flex-shrink-0" />
            <span className="text-truncate">
              {scopedToFolder ? 'Showing sources in ' : 'Showing all sources — this report is in '}
              <span className="fw-semibold" title={scopeLabel}>{scopeLabel}</span>
              {scopedToFolder && reportFolder && <span className="text-muted"> and its subfolders</span>}
            </span>
            {folderScopeAvailable && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-auto flex-shrink-0 text-nowrap"
                onClick={() => setScope(scopedToFolder ? SOURCE_SCOPE.all : SOURCE_SCOPE.folder)}
              >
                {scopedToFolder ? 'Show all' : 'Only this folder'}
              </button>
            )}
          </div>
        )}

        <div className="d-flex gap-2 mb-3" role="group" aria-label="Data source type">
          {[
            { kind: SOURCE_KIND.view, label: 'Saved views', icon: faTable },
            { kind: SOURCE_KIND.sample, label: 'Samples', icon: faClipboardCheck }
          ].map(({ kind, label, icon }) => (
            <Button
              key={kind}
              variant={sourceKind === kind ? 'primary' : 'light'}
              className={sourceKind === kind ? 'd-flex align-items-center gap-2' : 'border d-flex align-items-center gap-2 text-primary'}
              onClick={() => setSourceKind(kind)}
              aria-pressed={sourceKind === kind}
            >
              <FontAwesomeIcon icon={icon} />
              {label}
            </Button>
          ))}
        </div>

        <div className="d-flex gap-2 mb-3">
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={showingSamples ? 'Search samples or sections...' : 'Search views or sections...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          {aiEnabled && !showingSamples && (
            <Button
              variant="light"
              className="border flex-shrink-0 d-flex align-items-center gap-2 text-primary text-nowrap"
              onClick={() => setShowAiCreator((v) => !v)}
              aria-expanded={showAiCreator}
            >
              <FontAwesomeIcon icon={faMagic} />
              Create a new data source
            </Button>
          )}
        </div>

        {aiEnabled && !showingSamples && showAiCreator && (
          <AiViewCreator reportId={reportId} onCreated={() => setShowAiCreator(false)} />
        )}

        {scopeReady && scopedToFolder && sourceItems.length === 0 && (
          <div className="text-center text-muted small py-4" data-testid="dataset-scope-empty">
            No {showingSamples ? 'samples' : 'views'} in {reportFolder ? 'this folder' : 'the top level'} yet.{' '}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => setScope(SOURCE_SCOPE.all)}
            >
              Show all {showingSamples ? 'samples' : 'views'}
            </button>
          </div>
        )}

        <Row className="g-3">
          {filteredItems.map((item) => {
            const isLinked = linkedMap.has(sourceKey(item.sourceType, item.id))
            return (
              <Col xs={12} md={6} key={sourceKey(item.sourceType, item.id)}>
                <div
                  role="button"
                  onClick={() => handleToggle(item)}
                  className={`rounded-3 p-3 h-100 ${
                    isLinked ? 'border border-primary border-2' : 'border'
                  }`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                        style={{
                          width: 36,
                          height: 36,
                          backgroundColor: isLinked ? '#1E2E48' : '#e9ecef'
                        }}
                      >
                        <FontAwesomeIcon
                          icon={item.sourceType === 'AuditReport' ? faClipboardCheck : faTable}
                          className={isLinked ? 'text-white' : 'text-muted'}
                          size="sm"
                        />
                      </div>
                      <div>
                        <div className="fw-semibold" style={{ fontSize: '0.9rem', color: '#1E2E48' }}>
                          {item.name}
                        </div>
                        {item.sectionName && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {item.sectionName}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-2 flex-shrink-0 ${
                        isLinked ? 'bg-primary border-primary' : 'bg-white border'
                      }`}
                      style={{ width: 22, height: 22 }}
                    >
                      {isLinked && (
                        <FontAwesomeIcon icon={faCheck} className="text-white" style={{ fontSize: '0.65rem' }} />
                      )}
                    </div>
                  </div>
                  {(item.sectionName || item.pills.length > 0 || itemFolderLabel(item)) && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {itemFolderLabel(item) && (
                        <span
                          className="text-muted border rounded-pill px-2 py-0"
                          style={{ fontSize: '0.72rem', display: 'inline-block' }}
                          title="Folder"
                        >
                          <FontAwesomeIcon icon={faFolderOpen} className="me-1" />
                          {itemFolderLabel(item)}
                        </span>
                      )}
                      {item.sectionName && (
                        <span
                          className="text-muted border rounded-pill px-2 py-0"
                          style={{ fontSize: '0.72rem', display: 'inline-block' }}
                        >
                          # {item.sectionName}
                        </span>
                      )}
                      {item.pills.map((pill) => (
                        <span
                          key={pill}
                          className="text-muted border rounded-pill px-2 py-0"
                          style={{ fontSize: '0.72rem', display: 'inline-block' }}
                        >
                          {pill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            )
          })}
        </Row>
      </Modal.Body>

      <Modal.Footer className="border-top px-4 py-3 d-flex justify-content-between">
        <span className="text-muted small" data-testid="dataset-counts">
          <strong>{datasets.length}</strong> linked
          {' · '}
          <strong>{sourceItems.length}</strong> {showingSamples ? 'samples' : 'views'} available
          {scopedToFolder && (reportFolder ? ' in this folder' : ' at the top level')}
        </span>
        <Button variant="primary" onClick={onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DatasetModal
